import React, { useState, useEffect } from 'react'
import { useImproveCycleStore } from '../../store/improveCycleStore'
import { Save, CheckCircle, AlertCircle, Plus, Trash2 } from 'lucide-react'

const HacerForm = ({ ciclo }) => {
  const { updateTareas, saveHacer, completarHacer, loading } = useImproveCycleStore()
  const [tareas, setTareas] = useState([])
  const [formData, setFormData] = useState({
    lecciones_aprendidas: '',
    kpi_final: ''
  })
  const [mensaje, setMensaje] = useState(null)

  useEffect(() => {
    if (ciclo?.fases?.hacer) {
      setTareas(ciclo.fases.hacer.tareas || [])
      setFormData({
        lecciones_aprendidas: ciclo.fases.hacer.lecciones_aprendidas || '',
        kpi_final: ciclo.fases.hacer.kpi_final || ''
      })
    }
  }, [ciclo])

  const handleTareaChange = (index, field, value) => {
    const newTareas = [...tareas]
    newTareas[index] = { ...newTareas[index], [field]: value }
    setTareas(newTareas)
  }

  const handleUpdateTareas = async () => {
    try {
      await updateTareas(ciclo.id, tareas)
      setMensaje({ type: 'success', text: 'Tareas actualizadas' })
      setTimeout(() => setMensaje(null), 3000)
    } catch (error) {
      setMensaje({ type: 'error', text: error.message })
    }
  }

  const handleSave = async () => {
    try {
      await saveHacer(ciclo.id, formData)
      setMensaje({ type: 'success', text: 'Guardado exitosamente' })
      setTimeout(() => setMensaje(null), 3000)
    } catch (error) {
      setMensaje({ type: 'error', text: error.message })
    }
  }

  const handleCompletar = async () => {
    try {
      await completarHacer(ciclo.id)
      setMensaje({ type: 'success', text: 'Fase Hacer completada. Fase Verificar desbloqueada!' })
    } catch (error) {
      setMensaje({ type: 'error', text: error.message })
    }
  }

  const porcentajeCompletadas = tareas.length > 0
    ? Math.round((tareas.filter(t => t.estado === 'completada').length / tareas.length) * 100)
    : 0

  const puedeCompletar = porcentajeCompletadas === 100 && formData.kpi_final && !ciclo?.fases?.hacer?.completado

  return (
    <div className="space-y-6">
      {mensaje && (
        <div className={`p-4 rounded-lg flex items-start gap-3 ${
          mensaje.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          {mensaje.type === 'success' ? (
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          )}
          <p className={mensaje.type === 'success' ? 'text-green-700' : 'text-red-700'}>
            {mensaje.text}
          </p>
        </div>
      )}

      {/* Progreso de Tareas */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="font-semibold">Progreso de Tareas</span>
          <span className="text-2xl font-bold text-blue-600">{porcentajeCompletadas}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div 
            className="bg-blue-500 h-3 rounded-full transition-all duration-300"
            style={{ width: `${porcentajeCompletadas}%` }}
          ></div>
        </div>
      </div>

      {/* Gestión de Tareas */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">Gestión de Tareas</h3>
        
        <div className="space-y-3">
          {tareas.map((tarea, index) => (
            <div key={tarea.id} className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-800">{tarea.titulo}</h4>
                  <p className="text-sm text-gray-600 mt-1">{tarea.descripcion}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Responsable: {tarea.responsable} | Fecha límite: {tarea.fecha_limite}
                  </p>
                </div>
                
                <select
                  value={tarea.estado || 'pendiente'}
                  onChange={(e) => handleTareaChange(index, 'estado', e.target.value)}
                  disabled={ciclo?.fases?.hacer?.completado}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100"
                >
                  <option value="pendiente">Pendiente</option>
                  <option value="en_curso">En Curso</option>
                  <option value="bloqueada">Bloqueada</option>
                  <option value="completada">Completada</option>
                </select>
              </div>
            </div>
          ))}
        </div>

        {!ciclo?.fases?.hacer?.completado && (
          <button
            onClick={handleUpdateTareas}
            disabled={loading}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300"
          >
            Actualizar Tareas
          </button>
        )}
      </div>

      {/* Documentación del Piloto */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">Documentación del Piloto</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lecciones Aprendidas
            </label>
            <textarea
              value={formData.lecciones_aprendidas}
              onChange={(e) => setFormData(prev => ({ ...prev, lecciones_aprendidas: e.target.value }))}
              disabled={ciclo?.fases?.hacer?.completado}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              placeholder="Documenta las lecciones aprendidas durante la ejecución..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              KPI Final del Piloto *
            </label>
            <input
              type="number"
              value={formData.kpi_final}
              onChange={(e) => setFormData(prev => ({ ...prev, kpi_final: e.target.value }))}
              disabled={ciclo?.fases?.hacer?.completado}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              placeholder="Ingresa el valor final del KPI"
            />
          </div>
        </div>

        {!ciclo?.fases?.hacer?.completado && (
          <button
            onClick={handleSave}
            disabled={loading}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300"
          >
            Guardar Documentación
          </button>
        )}
      </div>

      {/* Botón Completar */}
      {!ciclo?.fases?.hacer?.completado && (
        <div className="flex gap-4">
          <button
            onClick={handleCompletar}
            disabled={loading || !puedeCompletar}
            className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <CheckCircle className="w-5 h-5" />
            Completar Ejecución
          </button>
          {!puedeCompletar && (
            <p className="text-sm text-gray-600 flex items-center">
              Completa todas las tareas (100%) e ingresa el KPI final
            </p>
          )}
        </div>
      )}

      {ciclo?.fases?.hacer?.completado && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-700 font-semibold">✓ Fase Hacer completada</p>
        </div>
      )}
    </div>
  )
}

export default HacerForm
